import { Trans, useTranslation } from 'react-i18next'

import LangLink from '../components/LangLink'
import Section from '../components/Section'

interface QA {
  q: string
  a: string
}

// Which FAQ answers carry an inline link, and where it points (keyed by the
// item's absolute index in faq.items, unchanged by the grouping below).
const ANSWER_LINKS: Record<number, string> = {
  0: '/products',
  10: '/partners/china',
}

// The 11 questions grouped along the buyer's path: what we sell, how an
// order and its money work, how it ships, and who stands behind it. Each
// entry is a list of absolute indices into faq.items; the labels live in
// faq.groups (same order).
const GROUPS: number[][] = [
  [0, 9],
  [1, 2, 5, 6],
  [3, 4, 7],
  [8, 10],
]

function FaqPage() {
  const { t } = useTranslation()
  const items = t('faq.items', { returnObjects: true }) as QA[]
  const groupTitles = t('faq.groups', { returnObjects: true }) as string[]

  return (
    <div className="faq-page">
      {/* ---------- header ---------- */}
      <Section background="surface" space="md" className="faq-hero">
        <p className="eyebrow">{t('faq.eyebrow')}</p>
        <h1 className="display">{t('faq.title')}</h1>
        <p className="faq-lead">
          <Trans
            i18nKey="faq.intro"
            components={{ lnk: <LangLink to="/contact" /> }}
          />
        </p>
      </Section>

      {/* ---------- grouped accordion ---------- */}
      <Section background="beige" space="lg" className="faq-body">
        {GROUPS.map((indices, gi) => (
          <div key={groupTitles[gi]} className="faq-group">
            <h2 className="faq-group-title">{groupTitles[gi]}</h2>
            <div className="faq-list">
              {indices.map((i) => (
                <details key={items[i].q}>
                  <summary>{items[i].q}</summary>
                  <div className="faq-answer">
                    <p>
                      <Trans
                        i18nKey={`faq.items.${i}.a`}
                        components={{
                          lnk: <LangLink to={ANSWER_LINKS[i] ?? '/'} />,
                        }}
                      />
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        ))}
      </Section>

      {/* ---------- still-have-a-question CTA ---------- */}
      <Section background="bordeaux" space="md" className="faq-cta">
        <h2>{t('faq.ctaHeading')}</h2>
        <p>{t('faq.ctaBody')}</p>
        <LangLink to="/contact" className="btn btn--on-dark">
          {t('faq.ctaButton')}
        </LangLink>
      </Section>
    </div>
  )
}

export default FaqPage
